import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import { X, Tag, RotateCcw, Check, Lock, Percent, Euro } from 'lucide-react';

const MESI = {
  1: 'Gennaio', 2: 'Febbraio', 3: 'Marzo', 4: 'Aprile', 5: 'Maggio', 6: 'Giugno',
  7: 'Luglio', 8: 'Agosto', 9: 'Settembre', 10: 'Ottobre', 11: 'Novembre', 12: 'Dicembre',
};

/**
 * Prezzi delle mensilità di un abbonamento.
 *
 * Ogni mese può avere una quota calcolata dal sistema oppure una decisa a
 * mano; in cima si applica uno sconto a tutte quelle ancora aperte. Le
 * mensilità già incassate non si toccano.
 */
const ModalPrezziMensilita = ({ abbonamento, onClose, onAggiornato }) => {
  const [rate, setRate] = useState([]);
  const [bozza, setBozza] = useState({});
  const [inCorso, setInCorso] = useState(null);

  // Sconto da applicare a tutte
  const [tipoSconto, setTipoSconto] = useState('PERCENTUALE');
  const [valoreSconto, setValoreSconto] = useState('');
  const [motivoSconto, setMotivoSconto] = useState('');
  const [applicando, setApplicando] = useState(false);

  useEffect(() => {
    if (!abbonamento) return;
    const elenco = abbonamento.rate || [];
    setRate(elenco);
    setBozza(Object.fromEntries(elenco.map((r) => [r.id, Number(r.importo).toFixed(2)])));
    setMotivoSconto(abbonamento.sconto_motivo || '');
    if (abbonamento.sconto_tipo && abbonamento.sconto_tipo !== 'NESSUNO') {
      setTipoSconto(abbonamento.sconto_tipo);
      setValoreSconto(String(Number(abbonamento.sconto_valore)));
    }
  }, [abbonamento]);

  if (!abbonamento) return null;

  const scontoAttivo = abbonamento.sconto_tipo && abbonamento.sconto_tipo !== 'NESSUNO';
  const totale = rate.reduce((a, r) => a + Number(r.importo), 0);
  const aperte = rate.filter((r) => !r.pagata);

  const ricarica = async () => {
    const res = await api.get(`/abbonamenti/abbonamenti/${abbonamento.id}/`);
    const nuove = res.data.rate || [];
    setRate(nuove);
    setBozza(Object.fromEntries(nuove.map((r) => [r.id, Number(r.importo).toFixed(2)])));
    onAggiornato?.();
  };

  const salvaPrezzo = async (rata) => {
    const nuovo = bozza[rata.id];
    if (nuovo === '' || Number(nuovo) === Number(rata.importo)) return;
    setInCorso(rata.id);
    const toastId = notify.loading('Salvataggio quota...');
    try {
      const res = await api.post(`/abbonamenti/rate/${rata.id}/imposta_prezzo/`, { importo: nuovo });
      notify.update(toastId, { type: 'success', render: res.data.messaggio });
      await ricarica();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nel salvataggio',
      });
    } finally {
      setInCorso(null);
    }
  };

  const tornaAutomatico = async (rata) => {
    setInCorso(rata.id);
    const toastId = notify.loading('Ripristino quota calcolata...');
    try {
      const res = await api.post(`/abbonamenti/rate/${rata.id}/prezzo_automatico/`);
      notify.update(toastId, { type: 'success', render: res.data.messaggio });
      await ricarica();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nel ripristino',
      });
    } finally {
      setInCorso(null);
    }
  };

  const applicaATutte = async (rimuovi = false) => {
    setApplicando(true);
    const toastId = notify.loading(rimuovi ? 'Rimozione sconto...' : 'Applicazione sconto...');
    try {
      const res = await api.post(`/abbonamenti/abbonamenti/${abbonamento.id}/applica_sconto/`, {
        tipo: rimuovi ? 'NESSUNO' : tipoSconto,
        valore: rimuovi ? 0 : valoreSconto,
        motivo: motivoSconto,
      });
      notify.update(toastId, { type: 'success', render: res.data.messaggio });
      if (rimuovi) setValoreSconto('');
      await ricarica();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'applicazione',
      });
    } finally {
      setApplicando(false);
    }
  };

  const manuali = rate.filter((r) => r.prezzo_manuale).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="border-b border-gray-200 px-5 py-3 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Prezzi delle mensilità</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {abbonamento.tratta || abbonamento.linea_nome} · {abbonamento.anno_scolastico_display}
              {' · '}totale €{totale.toFixed(2)}
              {manuali > 0 && ` · ${manuali} ${manuali === 1 ? 'quota impostata' : 'quote impostate'} a mano`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sconto su tutte le mensilità aperte */}
        <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 shrink-0">
          <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" />
            Applica a tutte le mensilità non ancora incassate
          </p>

          <div className="flex flex-wrap items-end gap-2">
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              <button
                type="button"
                onClick={() => setTipoSconto('PERCENTUALE')}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                  tipoSconto === 'PERCENTUALE' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
                }`}
              >
                <Percent className="w-3.5 h-3.5" /> %
              </button>
              <button
                type="button"
                onClick={() => setTipoSconto('IMPORTO')}
                className={`px-3 py-2 text-sm font-medium flex items-center gap-1 ${
                  tipoSconto === 'IMPORTO' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
                }`}
              >
                <Euro className="w-3.5 h-3.5" /> Importo
              </button>
            </div>

            <input
              type="number"
              min="0"
              step={tipoSconto === 'PERCENTUALE' ? 1 : 0.01}
              value={valoreSconto}
              onChange={(e) => setValoreSconto(e.target.value)}
              placeholder={tipoSconto === 'PERCENTUALE' ? '10' : '50.00'}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />

            <input
              type="text"
              value={motivoSconto}
              onChange={(e) => setMotivoSconto(e.target.value)}
              placeholder="Motivo (es. secondo figlio)"
              className="flex-1 min-w-[160px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />

            <button
              type="button"
              onClick={() => applicaATutte(false)}
              disabled={applicando || !valoreSconto || Number(valoreSconto) <= 0}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 transition disabled:opacity-40"
            >
              Applica
            </button>
          </div>

          {scontoAttivo && (
            <div className="flex items-center gap-3 mt-2">
              <p className="text-xs text-blue-800">
                Sconto attivo: <strong>{abbonamento.sconto_descrizione}</strong>
              </p>
              <button
                type="button"
                onClick={() => applicaATutte(true)}
                disabled={applicando}
                className="text-xs text-red-600 hover:underline"
              >
                Rimuovi
              </button>
            </div>
          )}
        </div>

        {/* Elenco delle mensilità */}
        <div className="overflow-y-auto divide-y divide-gray-100">
          {rate.map((rata) => {
            const modificato = bozza[rata.id] !== undefined
              && Number(bozza[rata.id]) !== Number(rata.importo);
            return (
              <div key={rata.id} className="px-5 py-3 flex flex-wrap items-center gap-3">
                <div className="w-28 shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{MESI[rata.mese]}</p>
                  <p className="text-[11px] text-gray-500">{rata.anno}</p>
                </div>

                <div className="flex-1 min-w-[130px]">
                  {rata.pagata ? (
                    <p className="text-sm font-bold text-green-700 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" />
                      €{Number(rata.importo).toFixed(2)} incassata
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="relative w-28">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={bozza[rata.id] ?? ''}
                          onChange={(e) => setBozza({ ...bozza, [rata.id]: e.target.value })}
                          onBlur={() => salvaPrezzo(rata)}
                          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                          disabled={inCorso === rata.id}
                          className="w-full pl-6 pr-2 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                        />
                      </div>
                      {modificato && (
                        <button
                          type="button"
                          onClick={() => salvaPrezzo(rata)}
                          className="p-1.5 text-green-700 hover:bg-green-50 rounded-lg"
                          title="Salva"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {rata.gratuita && !rata.prezzo_manuale ? (
                    <span className="px-2 py-1 text-[11px] rounded-full bg-blue-100 text-blue-800 font-medium">
                      gratuita
                    </span>
                  ) : rata.prezzo_manuale ? (
                    <>
                      <span className="px-2 py-1 text-[11px] rounded-full bg-amber-100 text-amber-800 font-medium">
                        manuale
                      </span>
                      {!rata.pagata && (
                        <button
                          type="button"
                          onClick={() => tornaAutomatico(rata)}
                          disabled={inCorso === rata.id}
                          title={`Torna alla quota calcolata (€${Number(rata.importo_automatico).toFixed(2)})`}
                          className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="px-2 py-1 text-[11px] rounded-full bg-gray-100 text-gray-600 font-medium">
                      automatica
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between shrink-0">
          <p className="text-xs text-gray-500">
            {aperte.length} da incassare · le mensilità già incassate non sono modificabili
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-semibold text-sm"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalPrezziMensilita;
